import type { FormEvent, ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { ArrowMore, Check16 } from '@elfsight/icons';
import { Show } from '@elfsight/lib-utils';
import {
  Dropdown as DropdownComponent,
  DropdownItem
} from '@elfsight/widget-modal';
import type {
  TextControlBaseInstance,
  TextControlProps
} from '@elfsight/widget-ui';
import { Icon, TextControl } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor,
  scrollbarColoredMixin
} from '@elfsight/widget-utils';
import type { FieldType } from '../../../contexts';
import type { CommonFieldProps } from '../../types';
import {
  useDropdownHandlers,
  useDropdownScroll,
  useDropdownState
} from './hooks';

export interface DropdownOption {
  id: string;
  label: string;
  value: string;
}

type TextDropdownFieldProps = TextControlProps &
  CommonFieldProps & {
    actualInputType: FieldType;
    isMobile?: boolean;
    disableDropdown?: boolean;
    disableAutoScroll?: boolean;
    options?: DropdownOption[];
    noOptionsText?: string;
    icon?: ReactNode;
    skipDisplayValueSync?: boolean;
    onOptionSelect?: (value?: string) => void;
    onInput?: (e: FormEvent<HTMLInputElement>) => void;
    parseInputValue?: (inputValue: string) => string;
    validateInput?: (inputValue: string) => boolean;
    formatValue?: (value: string) => string;
    findNearestOption?: (inputValue: string) => DropdownOption | undefined;
  };

export function TextDropdownField({
  id,
  label,
  placeholder = '',
  options = [],
  value,
  initialValue,
  required,
  showLabel = true,
  disabled = false,
  className,
  fieldActiveColor,
  fieldBorderType,
  fieldBorderWidth,
  fieldBorderColor,
  fieldBorderRadius,
  fieldLabelPosition,
  fieldLabelFontStyle,
  fieldLabelColor,
  fieldColor: fontColor,
  fieldFillColor,
  fieldFontStyle,
  type = 'text',
  inputMode = 'text',
  disableDropdown = false,
  disableAutoScroll = false,
  actualInputType,
  isMobile = false,
  skipDisplayValueSync = false,
  icon: IconComponent,
  noOptionsText = 'No options',
  onChange,
  onOptionSelect,
  onFocus,
  onBlur,
  onInput,
  parseInputValue,
  validateInput,
  formatValue,
  findNearestOption,
  ...forwardingProps
}: TextDropdownFieldProps) {
  const anchorElementRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const iconRef = useRef<HTMLDivElement | null>(null);
  const inputControlRef = useRef<TextControlBaseInstance | null>(null);

  const anchorWidth = anchorElementRef?.current?.getBoundingClientRect().width;
  const borderColor = fieldBorderType ? fieldBorderColor : 'transparent';
  const withUnderline = fieldBorderType === 'underline';
  const shouldDisableOnChange = actualInputType === 'time' && isMobile;
  const actualLabel =
    showLabel && fieldLabelPosition === 'inside' ? label : undefined;

  const {
    displayedValue,
    setDisplayedValue,
    localValue,
    setLocalValue,
    focused,
    setFocused,
    open,
    setOpen
  } = useDropdownState({
    value,
    initialValue,
    options,
    skipDisplayValueSync
  });

  const nearestOption = useMemo(() => {
    if (!findNearestOption || !localValue) {
      return;
    }

    return findNearestOption(localValue);
  }, [findNearestOption, localValue]);

  const {
    handleOpen,
    handleDropdownCloseRequest,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleChange,
    handleSelectOption,
    highlightedIndex
  } = useDropdownHandlers({
    anchorElementRef,
    iconRef,
    inputControlRef,
    nearestOption,
    actualInputType,
    shouldDisableOnChange,
    localValue,
    disableDropdown,
    options,
    open,
    setOpen,
    setFocused,
    setDisplayedValue,
    setLocalValue,
    parseInputValue,
    validateInput,
    formatValue,
    onChange,
    onOptionSelect,
    onFocus,
    onBlur
  });

  useDropdownScroll({
    contentRef,
    open,
    localValue,
    options,
    nearestOption,
    disableAutoScroll: disableAutoScroll,
    highlightedIndex
  });
  return (
    <>
      <Container ref={anchorElementRef} className={className}>
        <StyledTextControl
          {...forwardingProps}
          ref={inputControlRef}
          id={id}
          type={type}
          inputMode={inputMode}
          label={actualLabel}
          labelFont={fieldLabelFontStyle}
          labelColor={fieldLabelColor}
          color={fontColor}
          background={fieldFillColor}
          font={fieldFontStyle}
          borderType={fieldBorderType ? fieldBorderType : undefined}
          borderColor={borderColor}
          borderWidth={fieldBorderWidth}
          borderRadius={fieldBorderRadius}
          value={displayedValue}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onInput={onInput}
          onClick={handleOpen}
          onValueChange={handleChange}
          $focused={focused}
          $activeColor={fieldActiveColor}
          $borderColor={borderColor}
          $withUnderline={withUnderline}
          $borderWidth={fieldBorderWidth}
        />

        <Show when={!!IconComponent || !disableDropdown}>
          <IconContainer ref={iconRef} $focused={focused} onClick={handleOpen}>
            <Show when={!IconComponent} fallback={IconComponent}>
              <Icon fill={fontColor} size={24} component={ArrowMore} />
            </Show>
          </IconContainer>
        </Show>
      </Container>

      <Show when={!disableDropdown}>
        <StyledDropdownComponent
          portalContainerElement={
            anchorElementRef.current ? anchorElementRef.current : undefined
          }
          onCloseRequest={handleDropdownCloseRequest}
          displayOver={false}
          width={anchorWidth ? anchorWidth + 'px' : '100%'}
          open={open && focused && (options.length > 0 || !!localValue)}
          anchorElement={anchorElementRef.current}
          fontColor={fontColor}
          borderRadius={fieldBorderRadius}
          fontSize={fieldFontStyle?.fontSize}
          fontWeight={fieldFontStyle?.fontWeight}
          aria-labelledby={id}
        >
          <Content ref={contentRef} role="listbox" $color={fontColor}>
            <Show
              when={options.length > 0}
              fallback={<DropdownItem disabled>{noOptionsText}</DropdownItem>}
            >
              {options.map(({ value, label }, index) => {
                const isHighlighted = index === highlightedIndex;

                return (
                  <DropdownItem
                    key={value}
                    selected={isHighlighted}
                    iconLeft={isHighlighted ? Check16 : IconPlaceholder}
                    data-value={value}
                    data-label={label}
                    onClick={() => {
                      handleSelectOption(value);
                    }}
                  >
                    {label}
                  </DropdownItem>
                );
              })}
            </Show>
          </Content>
        </StyledDropdownComponent>
      </Show>
    </>
  );
}

const StyledDropdownComponent = styled(DropdownComponent)<{
  fontColor?: string;
}>`
  max-width: unset;
  overflow: hidden;
  padding: 0;
  background-color: ${({ fontColor = '#000' }) =>
    getInvertedColor(fontColor, ['rgb(255, 255, 255)', '#292929'])};
`;

const Content = styled.div.attrs<{ $color?: string }>({
  className: createReadableClass({
    component: 'fields-text-dropdown',
    element: 'content'
  })
})`
  overflow-y: auto;
  max-height: 150px;
  padding: 8px 0;

  ${({ $color = 'rgb(17, 17, 17)' }) => css`
    @supports not selector(-webkit-scrollbar) {
      scrollbar-width: thin;
      scrollbar-color: ${getColorWithAlpha($color, 0.1)} transparent;
    }

    ${scrollbarColoredMixin(getColorWithAlpha($color, 0.1))};

    &::-webkit-scrollbar-thumb:hover {
      background-color: ${getColorWithAlpha($color, 0.3)};
    }
  `}
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'fields-text-dropdown',
    element: 'container'
  })
})`
  position: relative;
  width: 100%;
`;

const StyledTextControl = styled(TextControl).attrs<{
  $focused?: boolean;
  $activeColor?: string;
  $borderColor?: string;
  $withUnderline?: boolean;
  $borderWidth?: number;
}>({
  className: createReadableClass({
    component: 'fields-text-dropdown',
    element: 'text-control'
  })
})`
  box-sizing: border-box;
  width: 100%;

  ${({ $withUnderline, $borderWidth }) =>
    $withUnderline
      ? css`
          border: none !important;
          border-bottom: ${$borderWidth}px solid !important;
        `
      : css`
          border: ${$borderWidth}px solid !important;
        `}

  border-color: ${({ $focused, $borderColor, $activeColor }) =>
    $focused ? $activeColor : $borderColor} !important;
`;

const IconContainer = styled.div.attrs<{ $focused: boolean }>({
  className: createReadableClass({
    component: 'fields-text-dropdown',
    element: 'icon-container'
  })
})`
  position: absolute;
  pointer-events: auto;
  z-index: 2;
  right: 12px;
  top: 50%;
  transform: translate(0, -50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $focused }) =>
    $focused &&
    css`
      transform: rotate(180deg) translate(0, 50%);
    `}
`;

const IconPlaceholder = styled.div.attrs({
  className: createReadableClass({
    component: 'fields-text-dropdown',
    element: 'icon-placeholder'
  })
})`
  width: 16px;
  background: none;
`;
